import React, { useEffect, useMemo, useState } from "react";
import AppShell from "@/components/layout/AppShell";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Edit, Trash2, Trophy, CalendarDays, MapPin, Clock, Home as HomeIcon, Plane, ClipboardCheck, Check, X as XIcon, MinusCircle } from "lucide-react";

const NUMERIC_ROUNDS = Array.from({ length: 24 }, (_, i) => String(i + 1));
const FINAL_ROUNDS = ["QF", "SF", "PF", "GF"];
const ALL_ROUNDS = [...NUMERIC_ROUNDS, ...FINAL_ROUNDS];

const HOME_AWAY = ["Home", "Away"];

const emptyFixture = {
  season: "",
  team_id: "",
  round: "1",
  date: "",
  time: "10:00",
  venue: "",
  home_away: "Home",
  opponent: "",
};

function uniqueSorted(arr) {
  return Array.from(new Set(arr.filter((v) => v !== undefined && v !== null && String(v).trim() !== "")))
    .sort((a, b) => String(a).localeCompare(String(b), undefined, { numeric: true }));
}

function fmtDate(iso) {
  if (!iso) return "";
  try {
    // Date-only string YYYY-MM-DD
    const [y, m, d] = iso.split("-").map((x) => parseInt(x, 10));
    if (!y || !m || !d) return iso;
    const dt = new Date(y, m - 1, d);
    return dt.toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short", year: "numeric" });
  } catch {
    return iso;
  }
}

function fmtTime12(t) {
  if (!t) return "";
  const [hStr, mStr] = t.split(":");
  let h = parseInt(hStr, 10);
  const m = (mStr || "00").padStart(2, "0");
  if (Number.isNaN(h)) return t;
  const suffix = h >= 12 ? "PM" : "AM";
  h = h % 12;
  if (h === 0) h = 12;
  return `${h}:${m} ${suffix}`;
}

function sortableKey(round) {
  // Sort by numeric round first, then finals in order
  const numericIdx = NUMERIC_ROUNDS.indexOf(round);
  if (numericIdx >= 0) return numericIdx;
  const finalIdx = FINAL_ROUNDS.indexOf(round);
  if (finalIdx >= 0) return 100 + finalIdx;
  return 999;
}

const ROUND_FULL_LABEL = {
  QF: "Quarter Final",
  SF: "Semi Final",
  PF: "Preliminary Final",
  GF: "Grand Final",
};

function roundLabel(r) {
  if (!r) return "";
  if (/^\d+$/.test(r)) return `Round ${r}`;
  return ROUND_FULL_LABEL[r] || r;
}

export default function AdminFixtures() {
  const [seasons, setSeasons] = useState([]);
  const [activeSeason, setActiveSeason] = useState("");
  const [teams, setTeams] = useState([]);
  const [fixtures, setFixtures] = useState([]);
  const [scope, setScope] = useState("upcoming"); // "upcoming" | "past"

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyFixture);
  const [confirmDel, setConfirmDel] = useState(null);
  const [roundOpen, setRoundOpen] = useState("");

  // Team Selection dialog state
  const [tsOpen, setTsOpen] = useState(false);
  const [tsFixture, setTsFixture] = useState(null);
  const [tsRows, setTsRows] = useState([]);
  const [tsCounts, setTsCounts] = useState({ available: 0, unavailable: 0, no_response: 0, total: 0 });
  const [tsLoading, setTsLoading] = useState(false);
  const [tsSaving, setTsSaving] = useState(false);
  // Editable selection state
  const [tsPlayerIds, setTsPlayerIds] = useState(new Set()); // playing XI
  const [tsEmergIds, setTsEmergIds] = useState(new Set());
  const [tsCaptain, setTsCaptain] = useState(null);
  const [tsViceCaptain, setTsViceCaptain] = useState(null);
  const [tsArrival, setTsArrival] = useState("");
  const [tsSavedAt, setTsSavedAt] = useState(null);
  // Which segment of members to show: "available" (with selection controls) | "unavailable" | "no_response"
  const [tsView, setTsView] = useState("available");

  const openTeamSelection = async (fixture) => {
    setTsFixture(fixture);
    setTsOpen(true);
    setTsLoading(true);
    setTsRows([]);
    setTsPlayerIds(new Set());
    setTsEmergIds(new Set());
    setTsCaptain(null);
    setTsViceCaptain(null);
    setTsArrival("");
    setTsSavedAt(null);
    setTsView("available");
    try {
      const [availRes, selRes] = await Promise.all([
        api.get(`/fixtures/${fixture.id}/availability/all`),
        api.get(`/fixtures/${fixture.id}/selection`),
      ]);
      setTsRows(availRes.data?.rows || []);
      setTsCounts(availRes.data?.counts || { available: 0, unavailable: 0, no_response: 0, total: 0 });
      const sel = selRes.data?.selection;
      if (sel) {
        setTsPlayerIds(new Set(sel.player_user_ids || []));
        setTsEmergIds(new Set(sel.emergency_user_ids || []));
        setTsCaptain(sel.captain_user_id || null);
        setTsViceCaptain(sel.vice_captain_user_id || null);
        setTsArrival(sel.arrival_time || "");
        setTsSavedAt(sel.saved_at || null);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load team selection");
    } finally {
      setTsLoading(false);
    }
  };

  // Helpers: row-level toggles
  const tsSetRole = (userId, role) => {
    // role: "play" | "emergency" | null
    const newP = new Set(tsPlayerIds);
    const newE = new Set(tsEmergIds);
    newP.delete(userId);
    newE.delete(userId);
    if (role === "play") newP.add(userId);
    if (role === "emergency") newE.add(userId);
    setTsPlayerIds(newP);
    setTsEmergIds(newE);
    // If user removed from play, clear captain/vice if they held it
    if (role !== "play") {
      if (tsCaptain === userId) setTsCaptain(null);
      if (tsViceCaptain === userId) setTsViceCaptain(null);
    }
  };

  const tsToggleCaptain = (userId) => {
    if (!tsPlayerIds.has(userId)) {
      toast.error("Captain must be selected as Play first");
      return;
    }
    if (tsCaptain === userId) {
      setTsCaptain(null);
    } else {
      setTsCaptain(userId);
      if (tsViceCaptain === userId) setTsViceCaptain(null);
    }
  };

  const tsToggleVice = (userId) => {
    if (!tsPlayerIds.has(userId)) {
      toast.error("Vice-Captain must be selected as Play first");
      return;
    }
    if (tsViceCaptain === userId) {
      setTsViceCaptain(null);
    } else {
      setTsViceCaptain(userId);
      if (tsCaptain === userId) setTsCaptain(null);
    }
  };

  const tsSave = async () => {
    if (!tsFixture) return;
    setTsSaving(true);
    try {
      await api.put(`/fixtures/${tsFixture.id}/selection`, {
        player_user_ids: Array.from(tsPlayerIds),
        emergency_user_ids: Array.from(tsEmergIds),
        captain_user_id: tsCaptain,
        vice_captain_user_id: tsViceCaptain,
        arrival_time: tsArrival || "",
      });
      toast.success("Team selection saved");
      setTsSavedAt(new Date().toISOString());
      // tell other parts of the app to refresh
      window.dispatchEvent(new CustomEvent("dcc:selection-saved"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save selection");
    } finally {
      setTsSaving(false);
    }
  };

  // Resolve the team (for grade/format display in the dialog header)
  const tsTeam = useMemo(() => {
    if (!tsFixture) return null;
    return teams.find((t) => t.id === tsFixture.team_id) || null;
  }, [tsFixture, teams]);

  // Row subsets keyed off availability status
  const tsAvailableRows = useMemo(() => tsRows.filter((r) => r.status === "available"), [tsRows]);
  const tsUnavailableRows = useMemo(() => tsRows.filter((r) => r.status === "unavailable"), [tsRows]);
  const tsNoResponseRows = useMemo(() => tsRows.filter((r) => !r.status), [tsRows]);

  // The selection list shows ONLY members who have marked themselves available.
  const tsSelectionViewRows = tsAvailableRows;

  // For the read-only roster views (Unavailable / Yet-to-Select), we still want to surface
  // any member who is currently saved in the selection so the admin can remove them.
  const tsRoleOf = (userId) => {
    if (tsCaptain === userId) return "Captain";
    if (tsViceCaptain === userId) return "Vice-Captain";
    if (tsPlayerIds.has(userId)) return "Playing";
    if (tsEmergIds.has(userId)) return "Emergency";
    return null;
  };

  const tsClearSelection = (userId) => {
    const newP = new Set(tsPlayerIds);
    const newE = new Set(tsEmergIds);
    newP.delete(userId);
    newE.delete(userId);
    setTsPlayerIds(newP);
    setTsEmergIds(newE);
    if (tsCaptain === userId) setTsCaptain(null);
    if (tsViceCaptain === userId) setTsViceCaptain(null);
  };

  const tsDisplayRows = tsView === "available"
    ? tsSelectionViewRows
    : tsView === "unavailable"
      ? tsUnavailableRows
      : tsNoResponseRows;

  // Count of currently-selected members who are no longer available (warning surface)
  const tsConflictCount = useMemo(() => {
    const selectedIds = new Set([...tsPlayerIds, ...tsEmergIds]);
    let count = 0;
    for (const r of tsRows) {
      if (selectedIds.has(r.user_id) && r.status !== "available") count++;
    }
    return count;
  }, [tsRows, tsPlayerIds, tsEmergIds]);

  /** Upcoming fixtures for the same team (excluding the currently selected fixture). */
  const upcomingTeamFixtures = useMemo(() => {
    if (!tsFixture) return [];
    const today = new Date().toISOString().slice(0, 10);
    return fixtures
      .filter((f) =>
        f.team_id === tsFixture.team_id &&
        f.id !== tsFixture.id &&
        f.date >= today
      )
      .sort((a, b) => {
        if (a.date !== b.date) return a.date.localeCompare(b.date);
        return sortableKey(a.round) - sortableKey(b.round);
      })
      .slice(0, 6);
  }, [fixtures, tsFixture]);

  const setF = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  // ---- loaders ----
  const loadSeasons = async () => {
    try {
      const { data } = await api.get("/seasons");
      setSeasons(data || []);
      if (!activeSeason && data && data.length > 0) {
        setActiveSeason(data[0].name);
      }
    } catch {
      toast.error("Failed to load seasons");
    }
  };

  const loadSeasonData = async (season) => {
    if (!season) { setTeams([]); setFixtures([]); return; }
    try {
      const [tRes, fRes] = await Promise.all([
        api.get(`/teams?season=${encodeURIComponent(season)}`),
        api.get(`/fixtures?season=${encodeURIComponent(season)}&scope=${scope}`),
      ]);
      setTeams(tRes.data || []);
      setFixtures(fRes.data || []);
    } catch {
      toast.error("Failed to load fixtures");
    }
  };

  useEffect(() => { loadSeasons(); }, []);
  useEffect(() => { loadSeasonData(activeSeason); }, [activeSeason, scope]);

  // ---- derived ----
  const venueSuggestions = useMemo(() => uniqueSorted(fixtures.map((f) => f.venue)), [fixtures]);
  const opponentSuggestions = useMemo(() => uniqueSorted(fixtures.map((f) => f.opponent)), [fixtures]);

  const sortedFixtures = useMemo(() => {
    return [...fixtures].sort((a, b) => {
      // Upcoming: soonest first (asc). Past/Results: most recent first (desc).
      const da = a.date || "";
      const dbb = b.date || "";
      if (da !== dbb) return scope === "past" ? dbb.localeCompare(da) : da.localeCompare(dbb);
      return sortableKey(a.round) - sortableKey(b.round);
    });
  }, [fixtures, scope]);

  // Group fixtures by round (rounds sorted in canonical order)
  const fixturesByRound = useMemo(() => {
    const m = new Map();
    for (const f of sortedFixtures) {
      if (!m.has(f.round)) m.set(f.round, []);
      m.get(f.round).push(f);
    }
    return Array.from(m.entries()).sort((a, b) => sortableKey(a[0]) - sortableKey(b[0]));
  }, [sortedFixtures]);

  const teamsById = useMemo(() => {
    const m = {};
    for (const t of teams) m[t.id] = t;
    return m;
  }, [teams]);

  // ---- actions ----
  const startNew = () => {
    if (!activeSeason) { toast.error("Select a season first"); return; }
    if (teams.length === 0) { toast.error("Create at least one team for this season first"); return; }
    setEditing(null);
    setForm({
      ...emptyFixture,
      season: activeSeason,
      team_id: teams[0]?.id || "",
    });
    setDialogOpen(true);
  };

  const startEdit = (f) => {
    setEditing(f);
    setForm({
      season: f.season,
      team_id: f.team_id,
      round: f.round,
      date: f.date,
      time: f.time || "10:00",
      venue: f.venue || "",
      home_away: f.home_away || "Home",
      opponent: f.opponent || "",
    });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.season) { toast.error("Season is required"); return; }
    if (!form.team_id) { toast.error("Select a team"); return; }
    if (!form.round || !ALL_ROUNDS.includes(form.round)) { toast.error("Pick a round"); return; }
    if (!form.date) { toast.error("Date is required"); return; }
    if (!form.time) { toast.error("Time is required"); return; }
    if (!HOME_AWAY.includes(form.home_away)) { toast.error("Pick Home or Away"); return; }

    const payload = {
      season: form.season,
      team_id: form.team_id,
      round: form.round,
      date: form.date,
      time: form.time,
      venue: (form.venue || "").trim(),
      home_away: form.home_away,
      opponent: (form.opponent || "").trim(),
    };
    try {
      if (editing) await api.put(`/fixtures/${editing.id}`, payload);
      else await api.post("/fixtures", payload);
      toast.success(editing ? "Fixture updated" : "Fixture created");
      setDialogOpen(false);
      loadSeasonData(activeSeason);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const removeFixture = async (id) => {
    try {
      await api.delete(`/fixtures/${id}`);
      toast.success("Fixture deleted");
      loadSeasonData(activeSeason);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <AppShell
      variant="admin"
      title="Fixtures"
      actions={
        <Button onClick={startNew} className="btn-accent-modern h-10" data-testid="fixtures-new" disabled={!activeSeason}>
          <Plus className="h-4 w-4 mr-2" /> Add fixture
        </Button>
      }
    >
      {/* Season bar */}
      <Card className="p-5 rounded-2xl mb-6">
        <div className="flex flex-col sm:flex-row sm:items-end gap-4">
          <div className="flex-1 min-w-0">
            <Label className="text-sm font-medium" htmlFor="fix-season-select">Season</Label>
            <div className="mt-1.5 flex items-center gap-2">
              <Select
                value={activeSeason || undefined}
                onValueChange={(v) => setActiveSeason(v)}
              >
                <SelectTrigger id="fix-season-select" data-testid="fixtures-season-select" className="h-11 rounded-lg w-full sm:w-[260px]">
                  <SelectValue placeholder={seasons.length ? "Select a season" : "No seasons yet — add one in Teams"} />
                </SelectTrigger>
                <SelectContent>
                  {seasons.map((s) => (
                    <SelectItem key={s.id} value={s.name} data-testid={`fixtures-season-option-${s.name}`}>
                      {s.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <p className="mt-2 text-xs text-muted-foreground inline-flex items-center gap-1">
              <CalendarDays className="h-3 w-3" /> Fixtures are organized by season. Teams must be added in the Teams tab first.
            </p>
          </div>
        </div>
      </Card>

      {/* Upcoming / Results scope tabs */}
      {activeSeason && (
        <ToggleGroup
          type="single"
          value={scope}
          onValueChange={(v) => v && setScope(v)}
          className="inline-flex mb-4 rounded-full border border-border p-1 bg-[hsl(var(--secondary))]/40"
          data-testid="fixtures-scope-toggle"
        >
          <ToggleGroupItem
            value="upcoming"
            aria-label="Upcoming fixtures"
            className="h-9 px-4 rounded-full text-xs font-semibold data-[state=on]:bg-[hsl(var(--accent))] data-[state=on]:text-white"
            data-testid="fixtures-scope-upcoming"
          >
            <CalendarDays className="h-3.5 w-3.5 mr-1.5" /> Upcoming
          </ToggleGroupItem>
        </ToggleGroup>
      )}

      {/* Fixtures list */}
      {!activeSeason ? (
        <Card className="p-14 text-center text-muted-foreground border-dashed rounded-2xl">
          <Trophy className="mx-auto h-8 w-8 mb-3 opacity-60" />
          Select or create a season to manage fixtures.
        </Card>
      ) : teams.length === 0 ? (
        <Card className="p-14 text-center text-muted-foreground border-dashed rounded-2xl">
          <Trophy className="mx-auto h-8 w-8 mb-3 opacity-60" />
          No teams in <span className="font-medium text-foreground">{activeSeason}</span> yet. Add a team in the <span className="font-medium text-foreground">Teams</span> tab first.
        </Card>
      ) : sortedFixtures.length === 0 ? (
        <Card className="p-14 text-center text-muted-foreground border-dashed rounded-2xl">
          <Trophy className="mx-auto h-8 w-8 mb-3 opacity-60" />
          {scope === "past" ? (
            <>No past fixtures in <span className="font-medium text-foreground">{activeSeason}</span> yet.</>
          ) : (
            <>No upcoming fixtures in <span className="font-medium text-foreground">{activeSeason}</span>. Click <span className="font-medium text-foreground">Add fixture</span> to create one.</>
          )}
        </Card>
      ) : (
        <Accordion
          type="single"
          collapsible
          value={roundOpen}
          onValueChange={setRoundOpen}
          className="space-y-3"
          data-testid="fixtures-rounds-accordion"
          onMouseLeave={() => setRoundOpen("")}
        >
          {fixturesByRound.map(([round, list]) => (
            <AccordionItem
              key={round}
              value={round}
              className="border border-border rounded-2xl bg-[hsl(var(--card))] overflow-hidden"
              data-testid={`round-card-${round}`}
            >
              <AccordionTrigger
                className="px-5 py-4 hover:no-underline hover:bg-[hsl(var(--secondary))] data-[state=open]:bg-[hsl(var(--secondary))]/60"
                data-testid={`round-trigger-${round}`}
              >
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div className="text-[10px] font-mono-sm uppercase tracking-[0.18em] text-[hsl(var(--accent))]">Round</div>
                  <div className="text-base sm:text-lg font-semibold tracking-tight truncate">{roundLabel(round)}</div>
                  <Badge variant="outline" className="ml-auto rounded-full text-[10px]" data-testid={`round-count-${round}`}>
                    {list.length} {list.length === 1 ? "fixture" : "fixtures"}
                  </Badge>
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-5">
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {list.map((f) => {
                    const team = teamsById[f.team_id];
                    const teamLabel = team?.name || f.team_name_snapshot || "Team";
                    return (
                      <Card key={f.id} className="p-5 rounded-2xl hover-lift transition-all" data-testid={`fixture-card-${f.id}`}>
                        <div className="text-lg font-semibold tracking-tight">
                          {f.home_away === "Home" ? (
                            <>{teamLabel} <span className="text-muted-foreground font-normal">vs</span> {f.opponent || "TBD"}</>
                          ) : (
                            <>{f.opponent || "TBD"} <span className="text-muted-foreground font-normal">vs</span> {teamLabel}</>
                          )}
                        </div>

                        <div className="mt-3 space-y-1.5 text-sm">
                          <div className="inline-flex items-center gap-1.5 text-muted-foreground">
                            <CalendarDays className="h-3.5 w-3.5" /> <span data-testid={`fixture-date-${f.id}`}>{fmtDate(f.date)}</span>
                          </div>
                          <div className="inline-flex items-center gap-1.5 text-muted-foreground ml-0">
                            <Clock className="h-3.5 w-3.5" /> <span data-testid={`fixture-time-${f.id}`}>{fmtTime12(f.time)}</span>
                          </div>
                          {f.venue && (
                            <div className="inline-flex items-center gap-1.5 text-muted-foreground">
                              <MapPin className="h-3.5 w-3.5" />
                              <span data-testid={`fixture-venue-${f.id}`}>{f.venue}</span>
                              <span className="inline-flex items-center gap-1 text-foreground font-medium" data-testid={`fixture-homeaway-${f.id}`}>
                                · {f.home_away === "Home" ? <HomeIcon className="h-3 w-3" /> : <Plane className="h-3 w-3" />}
                                {f.home_away}
                              </span>
                            </div>
                          )}
                        </div>

                        {team && (
                          <div className="mt-3 text-xs text-muted-foreground">
                            <span className="font-medium text-foreground">{team.name}</span>
                            {team.grade ? <> · Grade {team.grade}</> : null}
                            {team.format ? <> · {team.format}</> : null}
                            {team.type ? <> · {team.type}</> : null}
                          </div>
                        )}

                        <div className="mt-4 flex gap-2">
                          <Button size="sm" variant="outline" className="rounded-full" onClick={() => startEdit(f)} data-testid={`fixture-edit-${f.id}`}>
                            <Edit className="h-3.5 w-3.5 mr-1" /> Edit
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => setConfirmDel({ id: f.id, label: `${roundLabel(f.round)} fixture` })}
                            data-testid={`fixture-del-${f.id}`}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                          {scope === "upcoming" && f.selection_open && (
                            <Button
                              size="sm"
                              variant="default"
                              className="rounded-full ml-auto bg-[hsl(var(--accent))] hover:bg-[hsl(var(--accent))]/90"
                              onClick={() => openTeamSelection(f)}
                              data-testid={`fixture-team-selection-${f.id}`}
                            >
                              <ClipboardCheck className="h-3.5 w-3.5 mr-1" /> Team Selection
                            </Button>
                          )}
                        </div>
                      </Card>
                    );
                  })}
                </div>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      )}

      {/* Create/Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="rounded-2xl max-w-lg">
          <DialogHeader><DialogTitle>{editing ? "Edit fixture" : "New fixture"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="text-xs text-muted-foreground">Season: <span className="font-medium text-foreground">{form.season}</span></div>

            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-sm font-medium">Round</Label>
                <Select value={form.round} onValueChange={(v) => setF("round", v)}>
                  <SelectTrigger className="mt-1.5 h-11 rounded-lg" data-testid="fixture-form-round">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {NUMERIC_ROUNDS.map((r) => <SelectItem key={r} value={r} data-testid={`fixture-form-round-option-${r}`}>{r}</SelectItem>)}
                    {FINAL_ROUNDS.map((r) => <SelectItem key={r} value={r} data-testid={`fixture-form-round-option-${r}`}>{r}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm font-medium">Team</Label>
                <Select value={form.team_id || undefined} onValueChange={(v) => setF("team_id", v)}>
                  <SelectTrigger className="mt-1.5 h-11 rounded-lg" data-testid="fixture-form-team">
                    <SelectValue placeholder="Select a team" />
                  </SelectTrigger>
                  <SelectContent>
                    {teams.map((t) => (
                      <SelectItem key={t.id} value={t.id} data-testid={`fixture-form-team-option-${t.id}`}>
                        {t.name}{t.grade ? ` · G${t.grade}` : ""}{t.type ? ` · ${t.type}` : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-sm font-medium">Date</Label>
                <Input
                  type="date"
                  value={form.date}
                  onChange={(e) => setF("date", e.target.value)}
                  className="mt-1.5 h-11 rounded-lg"
                  data-testid="fixture-form-date"
                />
              </div>
              <div>
                <Label className="text-sm font-medium">Time</Label>
                <Input
                  type="time"
                  value={form.time}
                  onChange={(e) => setF("time", e.target.value)}
                  className="mt-1.5 h-11 rounded-lg"
                  data-testid="fixture-form-time"
                />
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-sm font-medium">Home / Away</Label>
                <Select value={form.home_away} onValueChange={(v) => setF("home_away", v)}>
                  <SelectTrigger className="mt-1.5 h-11 rounded-lg" data-testid="fixture-form-homeaway">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {HOME_AWAY.map((v) => <SelectItem key={v} value={v} data-testid={`fixture-form-homeaway-option-${v.toLowerCase()}`}>{v}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm font-medium">Venue</Label>
                <Input
                  value={form.venue}
                  onChange={(e) => setF("venue", e.target.value)}
                  placeholder="e.g. TCA Ground"
                  list="fixture-venue-suggestions"
                  className="mt-1.5 h-11 rounded-lg"
                  data-testid="fixture-form-venue"
                />
                <datalist id="fixture-venue-suggestions">
                  {venueSuggestions.map((v) => <option key={v} value={v} />)}
                </datalist>
              </div>
            </div>

            <div>
              <Label className="text-sm font-medium">Opponent</Label>
              <Input
                value={form.opponent}
                onChange={(e) => setF("opponent", e.target.value)}
                placeholder="e.g. North Hobart"
                list="fixture-opponent-suggestions"
                className="mt-1.5 h-11 rounded-lg"
                data-testid="fixture-form-opponent"
              />
              <datalist id="fixture-opponent-suggestions">
                {opponentSuggestions.map((v) => <option key={v} value={v} />)}
              </datalist>
            </div>

            <Button onClick={save} className="w-full h-11 btn-primary-modern" data-testid="fixture-form-save">
              {editing ? "Save changes" : "Create fixture"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Team Selection dialog */}
      <Dialog open={tsOpen} onOpenChange={setTsOpen}>
        <DialogContent className="rounded-2xl max-w-3xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardCheck className="h-5 w-5 text-[hsl(var(--accent))]" />
              Team Selection
              {tsTeam?.grade && (
                <Badge variant="outline" className="rounded-full text-[10px] ml-1">Grade {tsTeam.grade}</Badge>
              )}
              {tsTeam?.format && (
                <Badge variant="outline" className="rounded-full text-[10px]">{tsTeam.format}</Badge>
              )}
              {tsTeam?.type && (
                <Badge variant="outline" className="rounded-full text-[10px]">{tsTeam.type}</Badge>
              )}
            </DialogTitle>
          </DialogHeader>
          {tsFixture && (
            <div className="text-sm text-muted-foreground -mt-2 mb-2">
              <span className="font-medium text-foreground">{roundLabel(tsFixture.round)}</span>
              {" · "}{fmtDate(tsFixture.date)} {fmtTime12(tsFixture.time)}
              {tsFixture.venue ? <> · {tsFixture.venue} · {tsFixture.home_away}</> : <> · {tsFixture.home_away}</>}
              {" · "}
              {tsFixture.home_away === "Home"
                ? <>{tsFixture.team_name_snapshot || "Team"} vs {tsFixture.opponent || "TBD"}</>
                : <>{tsFixture.opponent || "TBD"} vs {tsFixture.team_name_snapshot || "Team"}</>}
            </div>
          )}

          {/* Upcoming rounds for this team */}
          {upcomingTeamFixtures.length > 0 && (
            <div className="rounded-xl border border-border bg-[hsl(var(--secondary))]/30 p-3 mb-3" data-testid="ts-upcoming-rounds">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium mb-2">Upcoming Matches</div>
              <div className="space-y-1.5">
                {upcomingTeamFixtures.map((f) => {
                  const isCurrentRound = tsFixture && f.round === tsFixture.round;
                  return (
                    <div
                      key={f.id}
                      className={`flex items-center gap-2.5 text-[13px] px-2.5 py-1.5 rounded-lg transition-colors ${
                        isCurrentRound ? "bg-[hsl(var(--accent))]/10 font-medium" : "hover:bg-muted/40"
                      }`}
                      data-testid={`ts-upcoming-${f.id}`}
                    >
                      <span className={`shrink-0 w-[70px] text-[11px] font-semibold ${isCurrentRound ? "text-[hsl(var(--accent))]" : "text-muted-foreground"}`}>
                        {roundLabel(f.round)}
                      </span>
                      <span className="shrink-0 text-muted-foreground text-[12px] w-[90px]">{fmtDate(f.date).replace(/,?\s*\d{4}$/, "")}</span>
                      <span className="min-w-0 truncate flex-1">
                        {f.home_away === "Home"
                          ? <>{f.team_name_snapshot || "Team"} <span className="text-muted-foreground">vs</span> {f.opponent || "TBD"}</>
                          : <>{f.opponent || "TBD"} <span className="text-muted-foreground">vs</span> {f.team_name_snapshot || "Team"}</>}
                      </span>
                      <Badge variant="outline" className={`shrink-0 rounded-full text-[9px] px-2 py-0 ${f.home_away === "Home" ? "border-emerald-500/40 text-emerald-600 dark:text-emerald-400" : "border-orange-500/40 text-orange-600 dark:text-orange-400"}`}>
                        {f.home_away}
                      </Badge>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Counts strip + arrival time — Available/Unavailable/Yet to Select are clickable tabs */}
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-3">
            <button
              type="button"
              onClick={() => setTsView("available")}
              className={`rounded-xl border px-3 py-2 text-center transition-all hover:bg-[hsl(var(--secondary))]/70 ${
                tsView === "available"
                  ? "border-[hsl(var(--accent))] bg-[hsl(var(--accent))]/10 ring-2 ring-[hsl(var(--accent))]/30"
                  : "border-border bg-[hsl(var(--secondary))]/50"
              }`}
              data-testid="ts-tab-available"
              aria-pressed={tsView === "available"}
            >
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Available</div>
              <div className="text-xl font-bold text-emerald-600 dark:text-emerald-500">{tsAvailableRows.length}</div>
            </button>

            <button
              type="button"
              onClick={() => setTsView("unavailable")}
              className={`rounded-xl border px-3 py-2 text-center transition-all hover:bg-[hsl(var(--secondary))]/70 ${
                tsView === "unavailable"
                  ? "border-destructive bg-destructive/10 ring-2 ring-destructive/30"
                  : "border-border bg-[hsl(var(--secondary))]/50"
              }`}
              data-testid="ts-tab-unavailable"
              aria-pressed={tsView === "unavailable"}
            >
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Unavailable</div>
              <div className="text-xl font-bold text-destructive">{tsUnavailableRows.length}</div>
            </button>

            <button
              type="button"
              onClick={() => setTsView("no_response")}
              className={`rounded-xl border px-3 py-2 text-center transition-all hover:bg-[hsl(var(--secondary))]/70 ${
                tsView === "no_response"
                  ? "border-[hsl(var(--accent))] bg-[hsl(var(--accent))]/10 ring-2 ring-[hsl(var(--accent))]/30"
                  : "border-border bg-[hsl(var(--secondary))]/50"
              }`}
              data-testid="ts-tab-no-response"
              aria-pressed={tsView === "no_response"}
            >
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Yet to Select</div>
              <div className="text-xl font-bold text-[hsl(var(--accent))]">{tsNoResponseRows.length}</div>
            </button>

            <div className="rounded-xl border border-border bg-[hsl(var(--secondary))]/50 px-3 py-2 text-center" data-testid="ts-count-selected">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Selected · Emerg</div>
              <div className="text-xl font-bold">{tsPlayerIds.size} <span className="text-muted-foreground font-medium">/ {tsEmergIds.size}</span></div>
            </div>

            <div className="rounded-xl border border-border px-3 py-2">
              <Label htmlFor="ts-arrival" className="text-[10px] uppercase tracking-wider text-muted-foreground">Arrive at ground</Label>
              <Input
                id="ts-arrival"
                type="time"
                value={tsArrival}
                onChange={(e) => setTsArrival(e.target.value)}
                className="mt-1 h-8 rounded-md text-sm"
                data-testid="ts-arrival-time"
              />
            </div>
          </div>

          {/* Legend (only shown in Available view, since selection controls are active there) */}
          {tsView === "available" && (
            <div className="flex flex-wrap items-center gap-3 text-[10px] uppercase tracking-wider text-muted-foreground pb-2 border-b border-border">
              <span className="inline-flex items-center gap-1"><span className="inline-block h-2 w-2 rounded-full bg-[hsl(var(--accent))]" /> Play</span>
              <span className="inline-flex items-center gap-1"><span className="inline-block h-2 w-2 rounded-full bg-foreground/60" /> Emerg</span>
              <span className="inline-flex items-center gap-1"><span className="inline-block h-4 w-4 rounded-full bg-[hsl(var(--accent))] text-white text-[9px] font-bold flex items-center justify-center">C</span> Captain</span>
              <span className="inline-flex items-center gap-1"><span className="inline-block h-4 w-4 rounded-full bg-foreground text-background text-[9px] font-bold flex items-center justify-center">V</span> Vice-Captain</span>
            </div>
          )}
          {tsView === "available" && tsConflictCount > 0 && (
            <div className="mt-2 px-3 py-2 rounded-lg border border-destructive/40 bg-destructive/10 text-xs text-destructive flex items-center gap-2" data-testid="ts-conflict-warning">
              <XIcon className="h-3.5 w-3.5 shrink-0" />
              {tsConflictCount} currently-selected {tsConflictCount === 1 ? "player is" : "players are"} no longer available.
              Open the <button type="button" onClick={() => setTsView("unavailable")} className="underline font-semibold">Unavailable</button> or <button type="button" onClick={() => setTsView("no_response")} className="underline font-semibold">Yet to Select</button> tab to remove them.
            </div>
          )}
          {tsView === "unavailable" && (
            <div className="text-xs text-muted-foreground pb-2 border-b border-border">
              These members have marked themselves <span className="font-medium text-destructive">Unavailable</span> for this fixture.
            </div>
          )}
          {tsView === "no_response" && (
            <div className="text-xs text-muted-foreground pb-2 border-b border-border">
              These members have <span className="font-medium text-[hsl(var(--accent))]">not yet responded</span> for this fixture.
            </div>
          )}

          {/* List */}
          <div className="mt-2 max-h-[44vh] overflow-y-auto rounded-xl border border-border">
            {tsLoading ? (
              <div className="px-4 py-10 text-center text-muted-foreground">Loading…</div>
            ) : tsDisplayRows.length === 0 ? (
              <div className="px-4 py-10 text-center text-muted-foreground">
                {tsView === "available" && "No members are available for this fixture."}
                {tsView === "unavailable" && "No members have marked themselves unavailable."}
                {tsView === "no_response" && "All members have responded."}
              </div>
            ) : tsView === "available" ? (
              <ul className="divide-y divide-border" data-testid="ts-rows">
                {tsDisplayRows.map((r) => {
                  const isPlay = tsPlayerIds.has(r.user_id);
                  const isEmerg = tsEmergIds.has(r.user_id);
                  const isCap = tsCaptain === r.user_id;
                  const isVice = tsViceCaptain === r.user_id;
                  const statusBadge =
                    r.status === "available" ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-500/15 text-emerald-600 dark:text-emerald-400" data-testid={`ts-row-status-${r.user_id}`}>
                        <Check className="h-3 w-3" /> Avail
                      </span>
                    ) : r.status === "unavailable" ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-destructive/15 text-destructive" data-testid={`ts-row-status-${r.user_id}`} title="Currently selected but marked unavailable">
                        <XIcon className="h-3 w-3" /> Unavail
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium text-muted-foreground border border-dashed border-border" data-testid={`ts-row-status-${r.user_id}`} title="Currently selected but no response">
                        <MinusCircle className="h-3 w-3" /> ?
                      </span>
                    );
                  return (
                    <li key={r.user_id} className={`flex items-center gap-3 px-3 py-2.5 ${isPlay ? "bg-[hsl(var(--accent))]/5" : isEmerg ? "bg-foreground/[0.03]" : ""}`} data-testid={`ts-row-${r.user_id}`}>
                      {r.avatar_url ? (
                        <img src={r.avatar_url} alt="" className="h-9 w-9 rounded-full object-cover border border-border flex-shrink-0" />
                      ) : (
                        <div className="h-9 w-9 rounded-full bg-[hsl(var(--secondary))] flex items-center justify-center text-sm font-semibold flex-shrink-0">
                          {(r.first_name?.[0] || "?").toUpperCase()}
                        </div>
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="font-medium truncate text-sm">{r.first_name} {r.last_name}</div>
                        <div className="text-[11px] text-muted-foreground truncate inline-flex items-center gap-2">
                          {r.member_id || "—"}{r.membership_type ? ` · ${r.membership_type}` : ""}
                          {statusBadge}
                        </div>
                      </div>
                      <ToggleGroup
                        type="single"
                        value={isPlay ? "play" : isEmerg ? "emergency" : ""}
                        onValueChange={(v) => tsSetRole(r.user_id, v || null)}
                        className="gap-1"
                        data-testid={`ts-role-${r.user_id}`}
                      >
                        <ToggleGroupItem value="play" aria-label="Play" className="h-8 px-2.5 rounded-md text-[11px] data-[state=on]:bg-[hsl(var(--accent))] data-[state=on]:text-white" data-testid={`ts-role-play-${r.user_id}`}>Play</ToggleGroupItem>
                        <ToggleGroupItem value="emergency" aria-label="Emergency" className="h-8 px-2.5 rounded-md text-[11px] data-[state=on]:bg-foreground data-[state=on]:text-background" data-testid={`ts-role-emerg-${r.user_id}`}>Emerg</ToggleGroupItem>
                      </ToggleGroup>
                      <div className="flex gap-1 ml-1">
                        <button type="button" onClick={() => tsToggleCaptain(r.user_id)} disabled={!isPlay} aria-label="Toggle Captain"
                          className={`h-8 w-8 rounded-full text-[11px] font-bold border transition-all disabled:opacity-30 disabled:cursor-not-allowed
                            ${isCap ? "bg-[hsl(var(--accent))] text-white border-[hsl(var(--accent))]" : "bg-transparent text-foreground border-border hover:border-[hsl(var(--accent))]"}`}
                          data-testid={`ts-captain-${r.user_id}`}>C</button>
                        <button type="button" onClick={() => tsToggleVice(r.user_id)} disabled={!isPlay} aria-label="Toggle Vice-Captain"
                          className={`h-8 w-8 rounded-full text-[11px] font-bold border transition-all disabled:opacity-30 disabled:cursor-not-allowed
                            ${isVice ? "bg-foreground text-background border-foreground" : "bg-transparent text-foreground border-border hover:border-foreground"}`}
                          data-testid={`ts-vice-${r.user_id}`}>V</button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            ) : (
              // Read-only roster view (Unavailable / Yet to Select). Members who are currently
              // saved in the selection get a warning badge + Remove button so the admin can drop them.
              <ul className="divide-y divide-border" data-testid={`ts-rows-${tsView}`}>
                {tsDisplayRows.map((r) => {
                  const currentRole = tsRoleOf(r.user_id);
                  return (
                    <li key={r.user_id} className={`flex items-center gap-3 px-3 py-2.5 ${currentRole ? "bg-destructive/5" : ""}`} data-testid={`ts-row-${r.user_id}`}>
                      {r.avatar_url ? (
                        <img src={r.avatar_url} alt="" className="h-9 w-9 rounded-full object-cover border border-border flex-shrink-0" />
                      ) : (
                        <div className="h-9 w-9 rounded-full bg-[hsl(var(--secondary))] flex items-center justify-center text-sm font-semibold flex-shrink-0">
                          {(r.first_name?.[0] || "?").toUpperCase()}
                        </div>
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="font-medium truncate text-sm">{r.first_name} {r.last_name}</div>
                        <div className="text-[11px] text-muted-foreground truncate inline-flex items-center gap-2 flex-wrap">
                          <span>{r.member_id || "—"}{r.membership_type ? ` · ${r.membership_type}` : ""}</span>
                          {currentRole && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-destructive/15 text-destructive">
                              Currently selected as {currentRole}
                            </span>
                          )}
                        </div>
                      </div>
                      {currentRole && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="rounded-full h-7 px-2.5 text-[11px] border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive"
                          onClick={() => tsClearSelection(r.user_id)}
                          data-testid={`ts-remove-${r.user_id}`}
                        >
                          Remove
                        </Button>
                      )}
                      {tsView === "unavailable" ? (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-destructive/15 text-destructive">
                          <XIcon className="h-3 w-3" /> Unavailable
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-medium text-muted-foreground border border-dashed border-border">
                          <MinusCircle className="h-3 w-3" /> No response
                        </span>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {/* Footer */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pt-3 border-t border-border">
            <div className="text-xs text-muted-foreground">
              {tsSavedAt
                ? <>Last saved {new Date(tsSavedAt).toLocaleString()}</>
                : "Not saved yet — selected members will be notified when you save."}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setTsOpen(false)} data-testid="ts-cancel">Close</Button>
              <Button
                onClick={tsSave}
                disabled={tsSaving || tsLoading}
                className="btn-primary-modern"
                data-testid="ts-save"
              >
                {tsSaving ? "Saving…" : "Save selection"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!confirmDel} onOpenChange={(o) => { if (!o) setConfirmDel(null); }}>
        <AlertDialogContent className="rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete fixture</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <span className="font-medium text-foreground">{confirmDel?.label}</span>? This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="fixtures-confirm-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="fixtures-confirm-delete"
              onClick={async () => {
                const c = confirmDel;
                setConfirmDel(null);
                if (c?.id) await removeFixture(c.id);
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppShell>
  );
}
